import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiList, apiPost } from '@/lib/api';
import type {
  AgeingReport,
  BankAccount,
  ChartAccount,
  ExchangeRate,
  Expense,
  ExpenseCategory,
  FinanceDashboard,
  Invoice,
  OpenInvoice,
  Payment,
  Product,
  ProfitAndLoss,
  RateQuote,
  Subscription,
  TaxRate,
  TrialBalance,
  Vendor,
} from './types';

function clean<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null),
  ) as Partial<T>;
}

/** Anything that moves money touches several of these at once. */
function invalidateFinance(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of ['invoices', 'payments', 'expenses', 'finance', 'subscriptions']) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

// ── Dashboard and reports ────────────────────────────────────────────────────

export function useFinanceDashboard() {
  return useQuery({
    queryKey: ['finance', 'dashboard'],
    queryFn: () => apiGet<FinanceDashboard>('/finance/dashboard'),
    staleTime: 60_000,
  });
}

export function useReceivablesAgeing() {
  return useQuery({
    queryKey: ['finance', 'ageing'],
    queryFn: () => apiGet<AgeingReport>('/finance/receivables-ageing'),
  });
}

export function useTrialBalance(params: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: ['finance', 'trial-balance', params],
    queryFn: () => apiGet<TrialBalance>('/finance/trial-balance', { params: clean(params) }),
  });
}

export function useProfitAndLoss(params: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: ['finance', 'pnl', params],
    queryFn: () => apiGet<ProfitAndLoss>('/finance/profit-and-loss', { params: clean(params) }),
  });
}

// ── Invoices ─────────────────────────────────────────────────────────────────

export function useInvoices(params: Record<string, unknown>) {
  return useQuery({
    queryKey: ['invoices', params],
    queryFn: () => apiList<Invoice[]>('/invoices', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ['invoices', id],
    queryFn: () => apiGet<Invoice>(`/invoices/${id}`),
    enabled: Boolean(id),
  });
}

export function useSaveInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Invoice>('/invoices', body),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useInvoiceFromDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dealId: string) => apiPost<Invoice>(`/invoices/from-deal/${dealId}`),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useApproveInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<Invoice>(`/invoices/${id}/approve`),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useSendInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<Invoice>(`/invoices/${id}/send`),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useVoidInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPost<Invoice>(`/invoices/${id}/void`, { reason }),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/invoices/${id}`),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

// ── Payments ─────────────────────────────────────────────────────────────────

export function usePayments(params: Record<string, unknown>) {
  return useQuery({
    queryKey: ['payments', params],
    queryFn: () => apiList<Payment[]>('/payments', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useOpenInvoices(companyId: string | undefined) {
  return useQuery({
    queryKey: ['payments', 'open-invoices', companyId],
    queryFn: () => apiGet<OpenInvoice[]>(`/payments/open-invoices/${companyId}`),
    enabled: Boolean(companyId),
  });
}

export function useRecordPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Payment>('/payments', body),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useAllocatePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; invoiceId: string; amount: string }) =>
      apiPost<Payment>(`/payments/${id}/allocate`, body),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useRefundPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; reason: string; amount?: string }) =>
      apiPost<Payment>(`/payments/${id}/refund`, body),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useSetChequeStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiPost<Payment>(`/payments/${id}/cheque-status`, { status }),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

// ── Expenses ─────────────────────────────────────────────────────────────────

export function useExpenses(params: Record<string, unknown>) {
  return useQuery({
    queryKey: ['expenses', params],
    queryFn: () => apiList<Expense[]>('/expenses', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useSaveExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Expense>('/expenses', body),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useApproveExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<Expense>(`/expenses/${id}/approve`),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useRejectExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPost<Expense>(`/expenses/${id}/reject`, { reason }),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/expenses/${id}`),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

// ── Catalogue and subscriptions ──────────────────────────────────────────────

export function useProducts() {
  return useQuery({ queryKey: ['products'], queryFn: () => apiGet<Product[]>('/products') });
}

export function useSaveProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Product>('/products', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useSavePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/products/plans', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useSubscriptions(params: Record<string, unknown>) {
  return useQuery({
    queryKey: ['subscriptions', params],
    queryFn: () => apiList<Subscription[]>('/subscriptions', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useCreateSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Subscription>('/subscriptions', body),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; reason: string; atPeriodEnd?: boolean }) =>
      apiPost<Subscription>(`/subscriptions/${id}/cancel`, body),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function usePauseSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      apiPost<Subscription>(`/subscriptions/${id}/pause`, { paused }),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

// ── Settings ─────────────────────────────────────────────────────────────────

export function useTaxRates() {
  return useQuery({
    queryKey: ['finance', 'tax-rates'],
    queryFn: () => apiGet<TaxRate[]>('/finance/tax-rates'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveTaxRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<TaxRate>('/finance/tax-rates', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['finance', 'tax-rates'] }),
  });
}

export function useChartAccounts() {
  return useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => apiGet<ChartAccount[]>('/finance/accounts'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<ChartAccount>('/finance/accounts', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['finance', 'accounts'] }),
  });
}

export function useBankAccounts() {
  return useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: () => apiGet<BankAccount[]>('/finance/bank-accounts'),
  });
}

export function useSaveBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<BankAccount>('/finance/bank-accounts', body),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

export function useVendors() {
  return useQuery({
    queryKey: ['finance', 'vendors'],
    queryFn: () => apiGet<Vendor[]>('/finance/vendors'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Vendor>('/finance/vendors', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['finance', 'vendors'] }),
  });
}

export function useExpenseCategories() {
  return useQuery({
    queryKey: ['finance', 'expense-categories'],
    queryFn: () => apiGet<ExpenseCategory[]>('/finance/expense-categories'),
    staleTime: 5 * 60 * 1000,
  });
}

/** Runs the billing jobs on demand, for finance to catch up or verify. */
export function useRunBilling() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<{
        subscriptions: { generated: number; failed: number };
        overdue: { marked: number };
        reminders: { sent: number };
      }>('/finance/run-billing'),
    onSuccess: () => invalidateFinance(queryClient),
  });
}

// ── Exchange rates ───────────────────────────────────────────────────────────

/** What we hold against one base currency, newest fetch first. */
export function useExchangeRates(base?: string) {
  return useQuery({
    queryKey: ['finance', 'exchange-rates', base ?? 'all'],
    queryFn: () => apiGet<ExchangeRate[]>('/finance/exchange-rates', { params: clean({ base }) }),
    staleTime: 5 * 60 * 1000,
  });
}

/** Pulls the feed now rather than waiting for the nightly job. */
export function useRefreshExchangeRates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (base?: string) =>
      apiPost<{ base: string; stored: number; fetchedAt: string | null }>(
        `/finance/exchange-rates/refresh${base ? `?base=${base}` : ''}`,
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['finance', 'exchange-rates'] }),
  });
}

/** One amount converted between two currencies, priced by the stored rate. */
export function useRateQuote(amount: string, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ['finance', 'convert', amount, from, to],
    queryFn: () => apiGet<RateQuote>('/finance/exchange-rates/convert', { params: { amount, from, to } }),
    enabled: enabled && Boolean(amount) && from !== to,
    staleTime: 5 * 60 * 1000,
  });
}
